import { Controller } from 'stimulus'
import { skeletonLoader } from '../utils/skeleton_loader'

export default class extends Controller {
  static targets = ['searchClauseContainer', 'addClauseButton', 'resetFiltersButton', 'filterSection', 'collapseIcon']

  initialize() {
    this.searchClauses = []
    this.searchOptions = {}
    this.inputTimer = null
    this.clauseIndex = 0
    this.submitTimer = null
    this.isResettingDependents = false
    this.isAddingClause = false
    this.templates = {} // Cache for loaded templates
  }

  // Load template from DOM and cache it
  loadTemplate(templateId) {
    if (this.templates[templateId]) {
      return this.templates[templateId]
    }

    const template = document.getElementById(templateId)
    if (!template) {
      console.error(`Template not found: ${templateId}`)
      return ''
    }

    this.templates[templateId] = template.innerHTML
    return this.templates[templateId]
  }

  // Render template with data replacements
  renderTemplate(templateId, data = {}) {
    let html = this.loadTemplate(templateId)

    // Replace all {{variableName}} placeholders with actual values
    Object.keys(data).forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g')
      // Use != null to handle 0 and false as valid values
      const value = data[key] != null ? data[key] : ''
      html = html.replace(regex, value)
    })

    return html
  }

  connect() {
    this.lockedDateKey = this.element.dataset.productsAdvancedFilterLockedDateKeyValue || null
    // Parse search options from data attributes
    this.searchOptions = JSON.parse(this.element.dataset.productsAdvancedFilterSearchOptionsValue || '{}')

    // Parse existing applied search clauses from data attributes
    const existingClauses = JSON.parse(this.element.dataset.productsAdvancedFilterSearchClausesAttributesValue || '[]')

    // Initialize search clauses
    const filteredExisting = Array.isArray(existingClauses) ? existingClauses.filter(clause => clause && clause.search_key) : []
    if (filteredExisting.length > 0) {
      this.searchClauses = filteredExisting.map(clause => this.initializeSearchClause(clause))
    } else {
      const initialSearchKeys = this.element.dataset.productsAdvancedFilterInitialSearchKeysValue || ''
      this.addInitialSearchClauses(initialSearchKeys)
    }

    // Only render if no clause pills exist (first load)
    const existingPills = this.searchClauseContainerTarget.querySelectorAll('[data-clause-index]')
    if (existingPills.length === 0) {
      this.renderSearchClauses()
    }

    // Set initial reset button visibility
    this.updateResetButtonVisibility()

    // Setup results section listeners for collapse
    this.setupResultsSectionListener()
  }

  disconnect() {
    if (this.inputTimer) {
      clearTimeout(this.inputTimer)
    }
    // Remove global click listener
    if (this.globalClickHandler) {
      document.removeEventListener('click', this.globalClickHandler)
    }
    // Remove touchstart listener
    if (this.touchStartHandler) {
      document.removeEventListener('touchstart', this.touchStartHandler)
    }
    // Remove scroll listener
    if (this.resultsScrollHandler) {
      const resultsSection = document.querySelector('.products-content-wrapper')
      if (resultsSection) {
        resultsSection.removeEventListener('scroll', this.resultsScrollHandler)
      }
    }
    if (this.submitTimer) {
      clearTimeout(this.submitTimer)
    }
  }

  // Initialize a search clause with proper structure
  initializeSearchClause(clause = {}) {
    const searchKey = clause.search_key || Object.keys(this.searchOptions)[0]
    const searchOption = this.searchOptions[searchKey]
    return {
      id: clause.id || null,
      search_key: searchKey,
      custom_attribute_id: clause.custom_attribute_id || searchOption?.custom_attribute_id || null,
      search_comparator: clause.search_comparator || searchOption?.comparators[0][0] || 'is',
      search_terms: clause.search_terms || clause.search_terms_attributes || [this.newSearchTerm()],
      _destroy: clause._destroy || null
    }
  }

  // Create a new search term
  newSearchTerm() {
    return {
      id: null,
      value: '',
      values: {},
      _destroy: null
    }
  }

  // Add initial search clauses based on initial search keys
  addInitialSearchClauses(initialSearchKeys) {
    const searchKeys = initialSearchKeys
      .split(',')
      .map(key => key.trim())
      .filter(key => key !== '')

    // If no valid initial keys provided, do not add any default clause
    if (searchKeys.length === 0) {
      return
    }

    searchKeys.forEach(searchKey => {
      if (this.searchOptions[searchKey]) {
        this.searchClauses.push(this.initializeSearchClause({ search_key: searchKey }))
      }
    })
  }

  // Render all search clauses
  renderSearchClauses() {
    const container = this.searchClauseContainerTarget

    // Remove only existing pills, not the "Add Filter" button
    container.querySelectorAll('[data-clause-index]').forEach(pill => pill.remove())

    // Get reference to "Add Filter" button (should be last child)
    const addButton = this.hasAddClauseButtonTarget ? this.addClauseButtonTarget : null

    // Insert pills before the "Add Filter" button
    this.searchClauses.forEach((clause, index) => {
      if (clause._destroy !== 1) {
        const clauseElement = this.createSearchClausePill(clause, index)
        // Always insert before the "Add Filter" button to keep it at the end
        if (addButton && addButton.parentNode === container) {
          container.insertBefore(clauseElement, addButton)
        } else {
          container.appendChild(clauseElement)
        }
      }
    })
    // Initialize event listeners for searchable selects after rendering
    this.initializeSearchableSelectListeners()

    // Scroll to the end to show the latest pill
    setTimeout(() => {
      container.scrollTo({
        left: container.scrollWidth,
        behavior: 'smooth'
      })
    }, 100)

    // Update reset button visibility
    this.updateResetButtonVisibility()
  }

  // Sync clause data from DOM inputs
  syncClauseDataFromDOM(clauseIndex) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause || clause._destroy === 1) return

    const existingElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (!existingElement) return

    // Sync search_key (support both long [search_key] and short [sk] names)
    const searchKeySelect = existingElement.querySelector(`select[name*="[search_key]"], select[name*="[sk]"]`)
    if (searchKeySelect) {
      clause.search_key = searchKeySelect.value
    }

    // Sync custom_attribute_id from hidden field
    const customAttributeIdInput = existingElement.querySelector(`input[name*="[custom_attribute_id]"]`)
    if (customAttributeIdInput) {
      clause.custom_attribute_id = customAttributeIdInput.value || null
    }

    // Sync search_comparator (support both long [search_comparator] and short [sc] names)
    const comparatorSelect = existingElement.querySelector(`select[name*="[search_comparator]"], select[name*="[sc]"]`)
    if (comparatorSelect) {
      clause.search_comparator = comparatorSelect.value
    }

    // Sync search_terms
    clause.search_terms.forEach((term, termIndex) => {
      if (term._destroy === 1) return

      // Initialize selected_texts if not exists
      if (!term.selected_texts) {
        term.selected_texts = {}
      }

      // Sync single value (can be input or select)
      const valueInput = existingElement.querySelector(`input[name*="[sta][${termIndex}][value]"]`)
      const valueSelect = existingElement.querySelector(`select[name*="[sta][${termIndex}][value]"]`)

      if (valueInput) {
        term.value = valueInput.value
      } else if (valueSelect) {
        term.value = valueSelect.value
        // Get display text from select option
        if (valueSelect.selectedOptions.length > 0) {
          term.selected_texts['value'] = valueSelect.selectedOptions[0].text
        }
      }

      // Sync multi-level values (compatibility) - can be inputs or selects
      // Check for both input and select elements
      const valuesInputs = existingElement.querySelectorAll(`input[name*="[sta][${termIndex}][values]"]`)
      const valuesSelects = existingElement.querySelectorAll(`select[name*="[sta][${termIndex}][values]"]`)

      // Sync from input elements (text, date, number fields)
      valuesInputs.forEach(input => {
        const match = input.name.match(/\[values\]\[(\w+)\]/)
        if (match) {
          const key = match[1]
          if (!term.values) term.values = {}
          term.values[key] = input.value
        }
      })

      // Sync from select elements (searchable selects)
      valuesSelects.forEach(selectElement => {
        const match = selectElement.name.match(/\[values\]\[(\w+)\]/)
        if (match) {
          const key = match[1]
          if (!term.values) term.values = {}
          term.values[key] = selectElement.value

          // Get the display text from the selected option
          if (selectElement.selectedOptions.length > 0) {
            term.selected_texts[key] = selectElement.selectedOptions[0].text
          }
        }
      })

      // Sync selected_texts from hidden inputs (if they exist - fallback)
      const selectedTextsInputs = existingElement.querySelectorAll(`input[name*="[sta][${termIndex}][selected_texts]"]`)
      selectedTextsInputs.forEach(input => {
        const match = input.name.match(/\[selected_texts\]\[(\w+)\]/)
        if (match) {
          term.selected_texts[match[1]] = input.value
        }
      })
    })
  }

  // Update pill label only (lightweight update)
  updatePillLabel(clauseIndex) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause || clause._destroy === 1) return

    // Sync data from DOM first
    this.syncClauseDataFromDOM(clauseIndex)

    // Find the existing clause element
    const existingElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (!existingElement) return

    // Update pill label
    const pillLabel = existingElement.querySelector(`[data-pill-label="${clauseIndex}"]`)
    if (pillLabel) {
      const newLabel = this.createPillLabel(clause, clauseIndex)
      pillLabel.innerHTML = newLabel
    }
  }

  // Update only a specific search clause without re-rendering everything
  updateSearchClause(clauseIndex) {
    const clause = this.searchClauses[clauseIndex]
    if (!clause || clause._destroy === 1) return

    // Find the existing clause element
    const existingElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (!existingElement) return

    // Update hidden fields FIRST (to reflect any changes in custom_attribute_id)
    const existingHiddenFields = existingElement.querySelectorAll('input[type="hidden"][name*="sca"]')
    existingHiddenFields.forEach(field => field.remove())
    existingElement.insertAdjacentHTML('afterbegin', this.createHiddenFields(clause, clauseIndex))

    // Update dropdown editor content (inside the dropdown, not the entire pill)
    const editorContent = existingElement.querySelector(`[data-clause-editor="${clauseIndex}"]`)
    if (editorContent) {
      editorContent.outerHTML = this.createPillEditor(clause, clauseIndex)
    }

    // Update pill label AFTER (syncs from the new DOM)
    this.updatePillLabel(clauseIndex)

    // Re-initialize listeners for this clause only
    this.initializeSearchableSelectListeners()
  }

  // Create pill label text
  createPillLabel(clause, index) {
    const searchOption = this.searchOptions[clause.search_key]
    const comparator = searchOption.comparators.find(c => c[0] === clause.search_comparator)
    const comparatorLabel = comparator ? comparator[1] : clause.search_comparator

    // Build pill label
    let pillLabel = `${searchOption.title} ${comparatorLabel}`

    // Add term values to label
    const activeTerms = clause.search_terms.filter(term => term._destroy !== 1)
    if (activeTerms.length > 0 && clause.search_comparator !== 'is_blank') {
      const termLabels = activeTerms.map(term => {
        if (term.values && Object.keys(term.values).length > 0) {
          // Handle 'between' comparator for dates (from/to)
          if (clause.search_comparator === 'between') {
            if (term.values.all_time === 'true' || term.values.all_time === true) {
              return 'All time'
            }
            if (term.values.from || term.values.to) {
              const fromDate = term.values.from || '...'
              const toDate = term.values.to || '...'
              return `${fromDate} and ${toDate}`
            }
            return '' // Return empty string if no dates set
          }
          // Handle date fields with other comparators (is, greater_than, etc.)
          // For date fields, check if values has from/to and use that instead of joining all values
          if (searchOption.type === 'date' && (term.values.from || term.values.to)) {
            return term.values.from || term.values.to || term.value
          }
          // Multi-level select (compatibility) - use selected_texts for display names
          const valueLabels = []
          Object.entries(term.values).forEach(([key, value]) => {
            if (value && key !== 'all_time') {
              // First try selected_texts (human-readable labels)
              if (term.selected_texts && term.selected_texts[key]) {
                valueLabels.push(term.selected_texts[key])
              }
              // Fallback to initial_options
              else if (term.initial_options && term.initial_options[key]) {
                const option = term.initial_options[key].find(opt => opt.id === value || opt.year === value)
                valueLabels.push(option ? (option.name || option.year || value) : value)
              }
              // Last resort: use the value itself
              else {
                valueLabels.push(value)
              }
            }
          })
          return valueLabels.join(' > ')
        } else {
          // Single value - use selected_texts or fallback to initial_options
          if (term.selected_texts && Object.keys(term.selected_texts).length > 0) {
            // For single selects, selected_texts might have the value under a key
            const textValue = Object.values(term.selected_texts)[0]
            return textValue || term.value
          } else if (term.initial_options && term.initial_options.length > 0) {
            const option = term.initial_options.find(opt => opt.id === term.value)
            return option ? option.name : term.value
          }
          return term.value
        }
      }).filter(label => label)

      if (termLabels.length > 0) {
        pillLabel += ` ${termLabels.join(' OR ')}`
      }
    }

    return pillLabel
  }

  // Create filter pill element for a search clause
  createSearchClausePill(clause, index) {
    const pillDiv = document.createElement('div')
    pillDiv.className = 'inline-block shrink-0'
    pillDiv.dataset.clauseIndex = index

    const pillLabel = this.createPillLabel(clause, index)
    const pillEditorContent = this.createPillEditor(clause, index)

    // Hide remove button for locked date filter (the primary date filter should not be removable)
    // Hide remove button for locked date filter if it's in position 0
    const removeButtonDisplay = (this.lockedDateKey && clause.search_key === this.lockedDateKey && index === 0) ? 'none' : 'flex'

    // Use template for pill rendering
    const pillHtml = this.renderTemplate('filter-pill-template', {
      clauseIndex: index,
      pillLabel: pillLabel,
      pillEditorContent: pillEditorContent,
      removeButtonDisplay: removeButtonDisplay
    })

    pillDiv.innerHTML = `
      ${this.createHiddenFields(clause, index)}
      ${pillHtml}
    `

    return pillDiv
  }

  // Create HTML element for a search clause (kept for backward compatibility)
  createSearchClauseElement(clause, index) {
    const clauseDiv = document.createElement('div')
    clauseDiv.className = 'bg-white rounded-lg border border-gray-200 shadow-sm p-3'
    clauseDiv.dataset.clauseIndex = index

    // Use clause wrapper template with placeholders
    let clauseHtml = this.renderTemplate('clause-wrapper-template', {})

    // Replace placeholders
    clauseHtml = clauseHtml.replace('<!--SEARCH_KEY_SELECT_PLACEHOLDER-->', this.createSearchKeySelect(clause, index))
    clauseHtml = clauseHtml.replace('<!--COMPARATOR_SELECT_PLACEHOLDER-->', this.createComparatorSelect(clause, index))
    clauseHtml = clauseHtml.replace('<!--TERMS_CONTAINER_PLACEHOLDER-->', this.createSearchTermsContainer(clause, index))
    clauseHtml = clauseHtml.replace('<!--REMOVE_BUTTON_PLACEHOLDER-->', this.createRemoveButton(index))

    clauseDiv.innerHTML = `
      ${this.createHiddenFields(clause, index)}
      ${clauseHtml}
    `

    return clauseDiv
  }

  // Create pill editor content (for dropdown/modal)
  createPillEditor(clause, index) {
    const searchOption = this.searchOptions[clause.search_key]

    // Use template for pill editor rendering with placeholders
    let html = this.renderTemplate('pill-editor-template', {
      clauseIndex: index,
      searchTermsContainer: this.createSearchTermsContainer(clause, index)
    })

    // Replace placeholders with actual select elements (after initial template rendering)
    html = html.replace('<!--SEARCH_KEY_SELECT_PLACEHOLDER-->', this.createSearchKeySelect(clause, index))
    html = html.replace('<!--COMPARATOR_SELECT_PLACEHOLDER-->', this.createComparatorSelect(clause, index))

    return html
  }

  // Create hidden form fields
  createHiddenFields(clause, index) {
    return `
      <input type="hidden" name="ps[sca][${index}][id]" value="${clause.id || ''}" />
      <input type="hidden" name="ps[sca][${index}][_destroy]" value="${clause._destroy || ''}" />
      ${clause.custom_attribute_id ? `<input type="hidden" name="ps[sca][${index}][custom_attribute_id]" value="${clause.custom_attribute_id}" />` : ''}
    `
  }

  // Create search key select dropdown
  createSearchKeySelect(clause, index) {
    // Separate standard and custom attribute options
    const entries = Object.entries(this.searchOptions)
    const standardOptions = entries.filter(([key, option]) => !option.custom_attribute_id)
    const customAttributeOptions = entries.filter(([key, option]) => option.custom_attribute_id)

    // Generate standard options (grouped under "Standard Filters")
    let standardHtml = ''
    if (standardOptions.length > 0) {
      const standardOptionsHtml = standardOptions
        .map(([key, option]) => `<option value="${key}" ${clause.search_key === key ? 'selected' : ''}>${option.title}</option>`)
        .join('')
      standardHtml = `<optgroup label="Standard Filters">${standardOptionsHtml}</optgroup>`
    }

    // Generate custom attribute options (grouped under "Custom Attributes")
    let customHtml = ''
    if (customAttributeOptions.length > 0) {
      const customOptionsHtml = customAttributeOptions
        .map(([key, option]) => `<option value="${key}" ${clause.search_key === key ? 'selected' : ''}>${option.title}</option>`)
        .join('')
      customHtml = `<optgroup label="Custom Attributes">${customOptionsHtml}</optgroup>`
    }

    const options = standardHtml + customHtml

    // Use template for search key select rendering
    return this.renderTemplate('search-key-select-template', {
      clauseIndex: index,
      options: options
    })
  }

  // Create comparator select dropdown
  createComparatorSelect(clause, index) {
    const searchOption = this.searchOptions[clause.search_key]
    const options = searchOption.comparators
      .map(([value, label]) => `<option value="${value}" ${clause.search_comparator === value ? 'selected' : ''}>${label}</option>`)
      .join('')

    // Use template for comparator select rendering
    return this.renderTemplate('comparator-select-template', {
      clauseIndex: index,
      options: options
    })
  }

  // Create search terms container
  createSearchTermsContainer(clause, index) {
    const termsHtml = clause.search_terms
      .map((term, termIndex) => this.createSearchTermElement(clause, index, term, termIndex))
      .join('')

    return this.renderTemplate('search-terms-container-template', {
      clauseIndex: index,
      termsHtml: termsHtml
    })
  }

  // Create individual search term element
  createSearchTermElement(clause, clauseIndex, term, termIndex) {
    if (term._destroy === 1) return ''

    const searchOption = this.searchOptions[clause.search_key]
    const isBlankComparator = clause.search_comparator === 'is_blank'

    if (isBlankComparator) {
      return `
        <input type="hidden" name="ps[sca][${clauseIndex}][sta][${termIndex}][id]" value="${term.id || ''}" />
        <input type="hidden" name="ps[sca][${clauseIndex}][sta][${termIndex}][_destroy]" value="${term._destroy || ''}" />
        ${this.renderTemplate('blank-comparator-template', {})}
      `
    }

    let inputElement = ''

    if (searchOption.type === 'select' && clause.search_comparator !== 'is_blank') {
      // Regular select dropdown with static options

      const options = searchOption.options
        .map(([value, label]) => `<option value="${value}" ${term.value.toString() === value.toString() ? 'selected' : ''}>${label}</option>`)
        .join('')

      inputElement = this.renderTemplate('select-input-template', {
        clauseIndex: clauseIndex,
        termIndex: termIndex,
        options: options
      })
    } else if (searchOption.type === 'multi-level-select' && clause.search_comparator !== 'is_blank') {
      // Multi-level select (compatibility) with selectize inputs
      inputElement = this.createMultiLevelSelectElement(clause, clauseIndex, term, termIndex)
    } else if (searchOption.endpoints && searchOption.endpoints[clause.search_comparator] && clause.search_comparator !== 'is_blank') {
      // Text field with endpoint (selectize input)
      inputElement = this.createSelectizeElement(clause, clauseIndex, term, termIndex)
    } else if (searchOption.type === 'date' && clause.search_comparator === 'between') {
      // Date between input (two date fields)
      const fromValue = term.values?.from || ''
      const toValue = term.values?.to || ''
      const allTimeValue = term.values?.all_time || ''
      const matchedRange = this.detectMatchingDateRange(fromValue, toValue, allTimeValue)
      const isAllTime = matchedRange === 'all_time'
      
      inputElement = this.renderTemplate('date-between-input-template', {
        clauseIndex: clauseIndex,
        termIndex: termIndex,
        fromValue: fromValue,
        toValue: toValue,
        allTimeValue: allTimeValue || '',
        dateInputsHiddenClass: isAllTime ? 'd-none' : '',
        allTimeSelected: isAllTime ? 'selected' : '',
        todaySelected: matchedRange === 'today' ? 'selected' : '',
        yesterdaySelected: matchedRange === 'yesterday' ? 'selected' : '',
        thisWeekSelected: matchedRange === 'this_week' ? 'selected' : '',
        lastWeekSelected: matchedRange === 'last_week' ? 'selected' : '',
        thisMonthSelected: matchedRange === 'this_month' ? 'selected' : '',
        lastMonthSelected: matchedRange === 'last_month' ? 'selected' : '',
        thisYearSelected: matchedRange === 'this_year' ? 'selected' : '',
        lastYearSelected: matchedRange === 'last_year' ? 'selected' : ''
      })
    } else if (searchOption.type === 'date') {
      // Date input
      inputElement = this.renderTemplate('date-input-template', {
        clauseIndex: clauseIndex,
        termIndex: termIndex,
        value: term.value || ''
      })
    } else if (searchOption.type === 'number') {
      // Number input
      inputElement = this.renderTemplate('number-input-template', {
        clauseIndex: clauseIndex,
        termIndex: termIndex,
        value: term.value || '',
        placeholder: 'Enter value'
      })
    } else if (clause.search_comparator !== 'is_blank') {
      // Default text input
      inputElement = this.renderTemplate('text-input-template', {
        clauseIndex: clauseIndex,
        termIndex: termIndex,
        value: term.value || '',
        placeholder: 'Enter value'
      })
    }

    // Use term wrapper template
    return this.renderTemplate('term-wrapper-template', {
      clauseIndex: clauseIndex,
      termIndex: termIndex,
      termId: term.id || '',
      destroy: term._destroy || '',
      orLabel: termIndex > 0 ? this.renderTemplate('or-label-template', {}) : '',
      inputElement: inputElement,
      buttons: this.createSearchTermButtons(clause, clauseIndex, termIndex)
    })
  }

  // Create searchable select element using HTML template
  createSelectizeElement(clause, clauseIndex, term, termIndex) {
    const searchOption = this.searchOptions[clause.search_key]
    const endpoint = searchOption.endpoints && searchOption.endpoints[clause.search_comparator]

    if (!endpoint) {
      console.error('No endpoint found for', clause.search_key, clause.search_comparator)
      return '<div class="flex-1"><input type="text" class="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1" placeholder="No endpoint configured" disabled /></div>'
    }

    const fieldName = `ps[sca][${clauseIndex}][sta][${termIndex}][value]`
    const fieldId = `search_clause_${clauseIndex}_term_${termIndex}_value`

    // Find selected term value's text
    let selectedText = '' // Default None of the term's value is selected
    if(term.initial_options && term.initial_options.length > 0){
      // Term's value is selected via suggestions
      selectedText = term.initial_options.find(option => option.id === term.value).name
    }

    // Use the HTML template and replace placeholders
    return this.renderSearchableSelectFromTemplate({
      fieldName: fieldName,
      fieldId: fieldId,
      endpoint: endpoint.url,
      valueField: endpoint.value_field || 'id',
      labelField: endpoint.label_field || 'name',
      selectedValue: term.value || '',
      selectedText: selectedText,
      clauseIndex: clauseIndex,
      searchKey: clause.search_key,
      placeholder: 'Type to search...'
    })
  }

  // Render searchable select using HTML template
  renderSearchableSelectFromTemplate(config) {
    const template = document.getElementById('searchable-select-template')
    if (!template) {
      console.error('Searchable select template not found')
      return '<div class="flex-1"><input type="text" class="px-3 py-2 border border-gray-300 rounded-md text-sm flex-1" placeholder="Template not found" disabled /></div>'
    }

    let html = template.innerHTML

    // Replace all placeholders with actual values
    html = html.replace(/{{fieldName}}/g, config.fieldName)
    html = html.replace(/{{fieldId}}/g, config.fieldId)
    html = html.replace(/{{endpoint}}/g, config.endpoint)
    html = html.replace(/{{valueField}}/g, config.valueField)
    html = html.replace(/{{labelField}}/g, config.labelField)
    html = html.replace(/{{selectedValue}}/g, config.selectedValue || '')
    html = html.replace(/{{selectedLabel}}/g, config.selectedText || config.placeholder || 'Search')
    html = html.replace(/{{placeholder}}/g, config.placeholder)
    html = html.replace(/{{dependentParam}}/g, config.dependentParam || '')
    html = html.replace(/{{dependentParamName}}/g, config.dependentParamName || '')
    html = html.replace(/{{otherDependentParam}}/g, config.otherDependentParam || '')
    html = html.replace(/{{otherDependentParamName}}/g, config.otherDependentParamName || '')

    // Add selected option to hidden select if we have both value and text
    if (config.selectedValue && config.selectedText) {
      html = html.replace(
        /<select([^>]*data-target="searchable-select\.hiddenSelect"[^>]*)>\s*<\/select>/,
        `<select$1><option value="${config.selectedValue}" selected>${config.selectedText}</option></select>`
      )
    }

    // Add data attributes for initial options if available
    if (config.initialOptions) {
      html = html.replace(
        /data-controller="([^"]*)"/,
        `data-controller="$1" data-initial-options='${config.initialOptions}'`
      )
    }

    // Note: Event listeners for searchable selects are handled by initializeSearchableSelectListeners()
    // The initial options and selected text will be processed when the searchable select is initialized

    // For single selects, wrap in flex-1 div
    if (!config.isMultiSelect) {
      return this.renderTemplate('searchable-select-wrapper-template', {
        selectKey: `${config.searchKey}_${config.clauseIndex}`,
        selectHtml: html
      })
    }

    return html
  }

  // Create multi-level select element (compatibility dropdowns)
  createMultiLevelSelectElement(clause, clauseIndex, term, termIndex) {
    const searchOption = this.searchOptions[clause.search_key]
    const selects = searchOption.selects || []

    const selectElements = selects.map((selectKey, selectIndex) => {
      const endpoint = searchOption.endpoints[clause.search_comparator][selectKey]
      const fieldName = `ps[sca][${clauseIndex}][sta][${termIndex}][values][${selectKey}]`
      const fieldId = `search_clause_${clauseIndex}_term_${termIndex}_${selectKey}`
      const currentValue = term.values && term.values[selectKey] ? term.values[selectKey] : ''
      const placeholder = this.getPlaceholderForSelectKey(selectKey)

      // Get initial options and selected text for this field
      const initialOptions = term.initial_options && term.initial_options[selectKey] ? term.initial_options[selectKey] : []
      const initialOptionsData = initialOptions.length > 0 ? JSON.stringify(initialOptions) : ''

      // Find selected term value's text
      let selectedText = '' // Default None of the term's value is selected

      if(initialOptions && initialOptions.length > 0){
        // Term's value is selected via suggestions
        if(initialOptions[0].year == currentValue){
          selectedText = currentValue
        } else {
          selectedText = initialOptions.find(option => option.id === currentValue).name
        }
      }

      // Use the same template approach as single searchable selects
      const selectHtml = this.renderSearchableSelectFromTemplate({
        fieldName: fieldName,
        fieldId: fieldId,
        endpoint: endpoint.url,
        valueField: endpoint.value_field || 'id',
        labelField: endpoint.label_field || 'name',
        selectedValue: currentValue,
        selectedText: selectedText,
        clauseIndex: clauseIndex,
        searchKey: selectKey,
        placeholder: placeholder,
        dependentParam: (endpoint.dependent_param_id ? `${endpoint.dependent_param_id}_${clauseIndex}_${termIndex}` : ''),
        dependentParamName: endpoint.dependent_param_id || '',
        otherDependentParam: (endpoint.other_dependent_param_id ? `${endpoint.other_dependent_param_id}_${clauseIndex}_${termIndex}` : ''),
        otherDependentParamName: endpoint.other_dependent_param_id || '',
        isMultiSelect: true,
        initialOptions: initialOptionsData
      })

      return this.renderTemplate('select-wrapper-template', {
        selectKey: `${selectKey}_${clauseIndex}_${termIndex}`,
        selectHtml: selectHtml
      })
    }).join('')

    // Wrap all selects in a responsive container that stacks on mobile
    return this.renderTemplate('multi-level-select-container-template', {
      selectElements: selectElements
    })
  }

  // Get placeholder text for select keys
  getPlaceholderForSelectKey(selectKey) {
    const placeholders = {
      make_id: 'Search makes...',
      model_id: 'Search models...',
      year: 'Search years...',
      sub_model_id: 'Search sub models...'
    }
    return placeholders[selectKey] || `Search ${selectKey}...`
  }

  // Create OR button element (generic method for reuse)
  createOrButton(clauseIndex) {
    const orBtn = document.createElement('button')
    orBtn.type = 'button'
    orBtn.className = 'px-3 py-2 text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-sm'
    orBtn.dataset.action = 'click->products--advanced-filter#addSearchTerm'
    orBtn.dataset.clauseIndex = String(clauseIndex)
    orBtn.textContent = '+OR'
    return orBtn
  }

  // Update term buttons for a clause (centralized logic)
  updateTermButtons(clauseIndex) {
    const clauseElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (!clauseElement) return

    const container = clauseElement.querySelector(`[data-search-terms-container="${clauseIndex}"]`)
    if (!container) return

    // Remove all existing OR buttons in this clause
    container.querySelectorAll(`button[data-action*="addSearchTerm"][data-clause-index="${clauseIndex}"]`).forEach(btn => btn.remove())

    // Get all visible term wrappers
    const wrappers = container.querySelectorAll('[data-term-wrapper]')
    if (wrappers.length === 0) return

    // Don't add OR button for 'between' or 'is_blank' comparators
    const clause = this.searchClauses[clauseIndex]
    if (clause && (clause.search_comparator === 'between' || clause.search_comparator === 'is_blank')) {
      return
    }

    // Add OR button to the last term only
    const lastWrapper = wrappers[wrappers.length - 1]
    const buttonsDiv = lastWrapper.querySelector('[data-term-buttons]')
    if (buttonsDiv && !buttonsDiv.querySelector('button[data-action*="addSearchTerm"]')) {
      const orBtn = this.createOrButton(clauseIndex)
      // Append after the × button (if it exists) instead of before
      buttonsDiv.appendChild(orBtn)
    }
  }

  // Create add/remove buttons for search terms
  createSearchTermButtons(clause, clauseIndex, termIndex) {
    const activeTerms = clause.search_terms.filter(term => term._destroy !== 1)
    const isLastTerm = termIndex === activeTerms.length - 1
    const canRemove = activeTerms.length > 1
    // Hide OR button for 'between' and 'is_blank' comparators
    const hideOrButton = clause.search_comparator === 'between' || clause.search_comparator === 'is_blank'

    // Use templates for button rendering
    const removeButton = canRemove ? this.renderTemplate('remove-term-button-template', {
      clauseIndex: clauseIndex,
      termIndex: termIndex
    }) : ''

    const orButton = (isLastTerm && !hideOrButton) ? this.renderTemplate('add-or-button-template', {
      clauseIndex: clauseIndex
    }) : ''

    return this.renderTemplate('term-buttons-template', {
      removeButton: removeButton,
      orButton: orButton
    })
  }

  // Create remove clause button
  createRemoveButton(index) {
    // Use template for remove clause button rendering
    return this.renderTemplate('remove-clause-button-template', {
      clauseIndex: index
    })
  }

  // Initialize event listeners for searchable select elements
  initializeSearchableSelectListeners() {
    // Find all hidden select elements within searchable select components
    const searchableSelects = this.element.querySelectorAll('[data-controller*="searchable-select"] select[data-target="searchable-select.hiddenSelect"]')

    searchableSelects.forEach(hiddenSelect => {
      // Remove any existing listeners to avoid duplicates
      hiddenSelect.removeEventListener('change', this.handleSearchableSelectChange.bind(this))

      // Add new listener
      hiddenSelect.addEventListener('change', this.handleSearchableSelectChange.bind(this))
    })
  }

  // Handle change events from searchable select components
  handleSearchableSelectChange(event) {
    const hiddenSelect = event.target
    const clauseIndex = this.getClauseIndexFromElement(hiddenSelect)

    // Skip form submission if we're in the middle of resetting dependent dropdowns
    if (this.isResettingDependents) {
      return
    }

    if (clauseIndex !== null) {
      this.checkAndSubmitForm(clauseIndex)
    }
  }

  // Get clause index from a form element
  getClauseIndexFromElement(element) {
    // Look for the clause container that contains this element
    const clauseContainer = element.closest('[data-clause-index]')
    if (clauseContainer) {
      return parseInt(clauseContainer.dataset.clauseIndex)
    }

    // Fallback: parse from the element's name attribute
    const nameMatch = element.name.match(/\[(\d+)\]/)
    if (nameMatch) {
      return parseInt(nameMatch[1])
    }

    return null
  }

  // Event handlers
  handleSearchKeyChange(event) {
    const clauseIndex = parseInt(event.target.dataset.clauseIndex)
    const newSearchKey = event.target.value
    const searchOption = this.searchOptions[newSearchKey]

    // Update the clause
    this.searchClauses[clauseIndex].search_key = newSearchKey
    this.searchClauses[clauseIndex].search_comparator = searchOption.comparators[0][0]
    this.searchClauses[clauseIndex].search_terms = [this.newSearchTerm()]
    // Update custom_attribute_id based on the new search key
    this.searchClauses[clauseIndex].custom_attribute_id = searchOption?.custom_attribute_id || null

    // Update only this specific clause
    this.updateSearchClause(clauseIndex)

    // Check if should auto-submit
    this.checkAndSubmitForm(clauseIndex)
  }

  handleComparatorChange(event) {
    const clauseIndex = parseInt(event.target.dataset.clauseIndex)

    this.searchClauses[clauseIndex].search_comparator = event.target.value
    // Reset search terms when condition changes
    this.searchClauses[clauseIndex].search_terms = [this.newSearchTerm()]

    // Update only this specific clause to show new search terms
    this.updateSearchClause(clauseIndex)

    // Check if should auto-submit
    this.checkAndSubmitForm(clauseIndex)
  }

  handleInputChange(event) {
    const clauseIndex = parseInt(event.target.dataset.clauseIndex)

    // Use debounce for text inputs
    if (this.inputTimer) {
      clearTimeout(this.inputTimer)
    }

    this.inputTimer = setTimeout(() => {
      // Update pill label
      this.updatePillLabel(clauseIndex)
      this.checkAndSubmitForm(clauseIndex)
    }, 500)
  }

  handleSelectChange(event) {
    const clauseIndex = parseInt(event.target.dataset.clauseIndex)

    // Update pill label
    this.updatePillLabel(clauseIndex)

    // For select elements, submit immediately
    this.checkAndSubmitForm(clauseIndex)
  }

  handleQuickDateSelect(event) {
    const select = event.target
    const range = select.value
    const clauseIndex = parseInt(select.dataset.clauseIndex)
    const termIndex = parseInt(select.dataset.termIndex)

    if (!range) return

    // Find the date inputs in the same container
    const container = select.closest('[data-term-wrapper]') || select.parentElement
    const fromInput = container.querySelector('input[data-field="from"]')
    const toInput = container.querySelector('input[data-field="to"]')
    const andLabel = container.querySelector('[data-date-and-label]') || container.querySelector('span.text-muted')

    // Handle "All time" by clearing the date values and storing 'all_time'
    if (range === 'all_time') {
      if (fromInput && toInput) {
        fromInput.value = ''
        toInput.value = ''

        // Hide the date inputs by adding d-none class
        fromInput.classList.add('d-none')
        toInput.classList.add('d-none')
        if (andLabel) andLabel.classList.add('d-none')

        // Update the search clause data with all_time marker
        if (this.searchClauses[clauseIndex] && this.searchClauses[clauseIndex].search_terms[termIndex]) {
          this.searchClauses[clauseIndex].search_terms[termIndex].values = {
            from: '',
            to: '',
            all_time: 'true'
          }
        }

        // Update hidden input for all_time (it should already exist from template)
        const allTimeInput = container.querySelector('input[data-field="all_time"]')
        if (allTimeInput) {
          allTimeInput.value = 'true'
        }

        // Update pill label and submit
        this.updatePillLabel(clauseIndex)
        this.checkAndSubmitForm(clauseIndex)
      }
      return
    }

    // Show the date inputs for non-all_time selections
    if (fromInput) fromInput.classList.remove('d-none')
    if (toInput) toInput.classList.remove('d-none')
    if (andLabel) andLabel.classList.remove('d-none')

    const { fromDate, toDate } = this.calculateDateRange(range)

    if (fromInput && toInput) {
      fromInput.value = fromDate
      toInput.value = toDate

      // Update the search clause data
      if (this.searchClauses[clauseIndex] && this.searchClauses[clauseIndex].search_terms[termIndex]) {
        this.searchClauses[clauseIndex].search_terms[termIndex].values = {
          from: fromDate,
          to: toDate
        }
      }

      // Clear all_time hidden input value if it exists
      const allTimeInput = container.querySelector('input[data-field="all_time"]')
      if (allTimeInput) {
        allTimeInput.value = ''
      }

      // Update pill label and submit
      this.updatePillLabel(clauseIndex)
      this.checkAndSubmitForm(clauseIndex)
    }
  }

  calculateDateRange(range) {
    const today = new Date()
    let fromDate, toDate

    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    switch(range) {
      case 'today':
        fromDate = toDate = formatDate(today)
        break
      case 'yesterday':
        const yesterday = new Date(today)
        yesterday.setDate(today.getDate() - 1)
        fromDate = toDate = formatDate(yesterday)
        break
      case 'this_week':
        const thisWeekStart = new Date(today)
        thisWeekStart.setDate(today.getDate() - today.getDay())
        fromDate = formatDate(thisWeekStart)
        toDate = formatDate(today)
        break
      case 'last_week':
        const lastWeekEnd = new Date(today)
        lastWeekEnd.setDate(today.getDate() - today.getDay() - 1)
        const lastWeekStart = new Date(lastWeekEnd)
        lastWeekStart.setDate(lastWeekEnd.getDate() - 6)
        fromDate = formatDate(lastWeekStart)
        toDate = formatDate(lastWeekEnd)
        break
      case 'this_month':
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)
        fromDate = formatDate(thisMonthStart)
        toDate = formatDate(today)
        break
      case 'last_month':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0)
        fromDate = formatDate(lastMonthStart)
        toDate = formatDate(lastMonthEnd)
        break
      case 'this_year':
        const thisYearStart = new Date(today.getFullYear(), 0, 1)
        fromDate = formatDate(thisYearStart)
        toDate = formatDate(today)
        break
      case 'last_year':
        const lastYearStart = new Date(today.getFullYear() - 1, 0, 1)
        const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31)
        fromDate = formatDate(lastYearStart)
        toDate = formatDate(lastYearEnd)
        break
      default:
        fromDate = toDate = formatDate(today)
    }

    return { fromDate, toDate }
  }

  detectMatchingDateRange(fromValue, toValue, allTimeValue) {
    // Check if all_time is set
    if (allTimeValue === 'true' || allTimeValue === true) {
      return 'all_time'
    }

    if (!fromValue || !toValue) return null

    // Check each range to see if it matches the given dates
    const ranges = ['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month', 'this_year', 'last_year']
    
    for (const range of ranges) {
      const { fromDate, toDate } = this.calculateDateRange(range)
      if (fromDate === fromValue && toDate === toValue) {
        return range
      }
    }

    return null
  }

  handleMultiSelectChange(event) {
    const clauseIndex = parseInt(event.target.dataset.clauseIndex)

    // Update pill label
    this.updatePillLabel(clauseIndex)

    // For multi-select elements, submit immediately
    this.checkAndSubmitForm(clauseIndex)
  }

  // Add new search clause
  addSearchClause() {
    // Prevent multiple simultaneous additions (iOS triple-click issue)
    if (this.isAddingClause) {
      return
    }

    this.isAddingClause = true

    const firstKey = Object.keys(this.searchOptions)[0]
    const newClause = this.initializeSearchClause({ search_key: firstKey })
    const newIndex = this.searchClauses.length

    this.searchClauses.push(newClause)

    // Just append the new clause pill without re-rendering everything
    const container = this.searchClauseContainerTarget
    const clauseElement = this.createSearchClausePill(newClause, newIndex)
    container.appendChild(clauseElement)

    // Initialize event listeners for any new searchable selects in this clause
    this.initializeSearchableSelectListeners()

    // Scroll to the end on mobile to show the new pill
    setTimeout(() => {
      container.scrollTo({
        left: container.scrollWidth,
        behavior: 'smooth'
      })
    }, 100)

    // Reset flag after a short delay to allow next click
    setTimeout(() => {
      this.isAddingClause = false
    }, 300)

    // Update reset button visibility
    this.updateResetButtonVisibility()
  }

  // Remove search clause
  removeSearchClause(event) {
    // Find the button element (in case SVG or path was clicked)
    const button = event.target.closest('button[data-clause-index]')
    if (!button) {
      console.error('Could not find button with clause index')
      return
    }

    const clauseIndex = parseInt(button.dataset.clauseIndex)

    // Prevent removal of locked date filter if it's in position 0
    const clause = this.searchClauses[clauseIndex]
    if (clause && this.lockedDateKey && clause.search_key === this.lockedDateKey && clauseIndex === 0) {
      console.warn('Locked date filter in position 0 cannot be removed')
      return
    }

    // Mark clause as destroyed in the data
    this.searchClauses[clauseIndex]._destroy = 1

    // Find the clause element directly from the container
    // This ensures we get the main clause div, not just the button
    const clauseElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)

    if (clauseElement) {
      clauseElement.remove()
    } else {
      console.error('Could not find clause element to remove:', clauseIndex)
      // Fallback to re-rendering if DOM removal fails
      this.renderSearchClauses()
    }

    // Auto-submit if not in modal
    if (!this.isInsideModal()) {
      this.submitForm()
    }

    // Update reset button visibility
    this.updateResetButtonVisibility()
  }

  // Add search term to clause
  addSearchTerm(event) {
    event.preventDefault()
    event.stopPropagation()

    const clauseIndex = parseInt(event.target.dataset.clauseIndex)
    const clause = this.searchClauses[clauseIndex]
    // Add new term to data model
    clause.search_terms.push(this.newSearchTerm())
    const newTermIndex = clause.search_terms.length - 1

    // Append only the new term's HTML to avoid resetting existing values
    const container = this.searchClauseContainerTarget.querySelector(`[data-search-terms-container="${clauseIndex}"]`)
    if (container) {
      // Ensure only the last term shows the OR button
      const existingOrButtons = container.querySelectorAll(`button[data-action*="addSearchTerm"][data-clause-index="${clauseIndex}"]`)
      existingOrButtons.forEach(btn => btn.remove())

      const newTermHtml = this.createSearchTermElement(clause, clauseIndex, clause.search_terms[newTermIndex], newTermIndex)
      const temp = document.createElement('div')
      temp.innerHTML = newTermHtml
      while (temp.firstChild) container.appendChild(temp.firstChild)

      // Initialize listeners for any searchable selects in the new term
      this.initializeSearchableSelectListeners()

      // Update term buttons using centralized logic
      this.updateTermButtons(clauseIndex)
    }
  }

  // Remove search term from clause
  removeSearchTerm(event) {
    // Find the button element (in case text content was clicked)
    const button = event.target.closest('button[data-clause-index][data-term-index]')
    if (!button) {
      console.error('Could not find button with clause and term index')
      return
    }

    const clauseIndex = parseInt(button.dataset.clauseIndex)
    const termIndex = parseInt(button.dataset.termIndex)

    this.searchClauses[clauseIndex].search_terms[termIndex]._destroy = 1

    // Update hidden _destroy input in DOM so form submits correctly
    const clauseElement = this.searchClauseContainerTarget.querySelector(`[data-clause-index="${clauseIndex}"]`)
    if (clauseElement) {
      const destroyInput = clauseElement.querySelector(`input[name="ps[sca][${clauseIndex}][sta][${termIndex}][_destroy]"]`)
      if (destroyInput) destroyInput.value = 1

      // Remove the visible term wrapper only
      const termWrapper = button.closest('[data-term-wrapper]')
      if (termWrapper) termWrapper.remove()

      // Update term buttons using centralized logic
      this.updateTermButtons(clauseIndex)
    }

    // Auto-submit if not in modal
    if (!this.isInsideModal()) {
      this.submitForm()
    }
  }

  // Check if search clause is complete and submit if needed
  checkAndSubmitForm(clauseIndex) {
    if (!this.isInsideModal()) {
      // Debounce form submission to prevent multiple simultaneous submissions
      this.debouncedSubmitForm()
    }
  }

  // Debounced form submission to handle multiple rapid changes
  debouncedSubmitForm() {
    // Clear any existing submit timer
    if (this.submitTimer) {
      clearTimeout(this.submitTimer)
    }

    // Set a new timer to submit after a short delay
    this.submitTimer = setTimeout(() => {
      // Double-check that we're not still resetting dependents
      if (this.isResettingDependents) {
        // Still resetting dependents, delaying submission...
        this.debouncedSubmitForm() // Retry after another delay
        return
      }

      // Debounced form submission executing...
      this.submitForm()
    }, 150) // 150ms delay to allow dependent dropdown changes to complete
  }

  // Check if inside modal
  isInsideModal() {
    return this.element.closest('.modal') !== null
  }

  // Submit the form
  submitForm() {
    // Show skeleton loaders before submission
    skeletonLoader.show({ position: 'prepend' })

    // Update all pill labels before submission
    this.searchClauses.forEach((clause, index) => {
      if (clause._destroy !== 1) {
        this.updatePillLabel(index)
      }
    })

    // Set flag to prevent dropdown from closing during auto-submit
    window.isAutoSubmitting = true

    const searchBtn = document.querySelector('.search-btn')
    if (searchBtn) {
      searchBtn.click()
    } else {
      // Fallback: submit the form directly
      const searchForm = document.getElementById('search-form-tab')
      if (searchForm) {
        searchForm.requestSubmit()
      }
    }
    // Clear flag after Turbo navigation completes
    setTimeout(() => {
      window.isAutoSubmitting = false
    }, 100)
  }

  resetFilters(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    // Keep the locked date filter if it's in position 0, remove all other search clauses
    const dateClause = this.lockedDateKey && this.searchClauses[0] && this.searchClauses[0].search_key === this.lockedDateKey && this.searchClauses[0]._destroy !== 1
      ? this.searchClauses[0]
      : null
    if (dateClause) {
      // Reset date filter to today's date
      const today = this.calculateDateRange('today')
      dateClause.search_terms = [{
        id: null,
        value: '',
        values: { from: today.fromDate, to: today.toDate },
        _destroy: null
      }]
      this.searchClauses = [dateClause]
    } else {
      this.searchClauses = []
    }
    this.renderSearchClauses()
    // Try to reuse the search controller to clear the main search input
    const searchController = this.application.getControllerForElementAndIdentifier(
      this.element,
      'search--product'
    )
    if (searchController && typeof searchController.clearInput === 'function') {
      // clearInput will also submit the form via its own logic
      searchController.clearInput()
    } else {
      // Fallback: clear the input and submit via this controller
      const searchInput = document.getElementById('product-search-input')
      if (searchInput) {
        searchInput.value = ''
      }
      const removeButton = this.element.querySelector('[data-target="search--product.removeButton"]')
      if (removeButton) {
        removeButton.classList.add('hidden')
      }
      this.submitForm()
    }
  }

  // Update reset button visibility based on active pills
  updateResetButtonVisibility() {
    if (!this.hasResetFiltersButtonTarget) return

    const activeClauses = this.searchClauses.filter(c => c._destroy !== 1)

    if (activeClauses.length > 0) {
      this.resetFiltersButtonTarget.classList.remove('hidden')
    } else {
      this.resetFiltersButtonTarget.classList.add('hidden')
    }
  }

  // Check if filter section should allow collapse
  shouldAllowCollapse() {
    const activeClauses = this.searchClauses.filter(c => c._destroy !== 1)
    return activeClauses.length > 0
  }

  // Expand filter section
  expandFilterSection() {
    if (!this.hasFilterSectionTarget) return
    this.filterSectionTarget.style.maxHeight = '1000px'
    // Hide collapse icon when expanded
    if (this.hasCollapseIconTarget) {
      this.collapseIconTarget.classList.add('hidden')
    }
  }

  // Collapse filter section (only if pills exist)
  collapseFilterSection() {
    if (!this.hasFilterSectionTarget) return
    if (!this.shouldAllowCollapse()) return
    this.filterSectionTarget.style.maxHeight = '0'
    // Show collapse icon when collapsed
    if (this.hasCollapseIconTarget) {
      this.collapseIconTarget.classList.remove('hidden')
    }
  }

  // Handle header click to expand filter
  handleHeaderClick(event) {
    // Don't expand if clicking on interactive elements
    const isInteractive = event.target.closest('button, a, input, select, textarea')
    if (isInteractive) return

    this.expandFilterSection()
  }

  // Setup listeners for results section click and scroll
  setupResultsSectionListener() {
    const resultsSection = document.querySelector('.products-content-wrapper')

    // Global click handler to collapse when clicking outside header
    this.globalClickHandler = (event) => {
      const header = document.querySelector('.app-header')
      // Collapse if clicking outside the header (fixed header section)
      if (header && !header.contains(event.target)) {
        this.collapseFilterSection()
      }
    }

    // Touch start handler for mobile swipe detection
    this.touchStartHandler = (event) => {
      const header = document.querySelector('.app-header')
      // Collapse if touch starts outside the header
      if (header && !header.contains(event.target)) {
        this.collapseFilterSection()
      }
    }

    // Scroll handler with debounce for results section
    if (resultsSection) {
      let scrollTimeout
      this.resultsScrollHandler = () => {
        clearTimeout(scrollTimeout)
        scrollTimeout = setTimeout(() => {
          this.collapseFilterSection()
        }, 150)
      }
      resultsSection.addEventListener('scroll', this.resultsScrollHandler, { passive: true })
    }

    // Add global click listener
    document.addEventListener('click', this.globalClickHandler)

    // Add touchstart listener for mobile swipe detection
    document.addEventListener('touchstart', this.touchStartHandler, { passive: true })
  }
}
